import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "344",
  "slug": "geography-bell-ringers-class-warm-ups",
  "title": "Geography Bell Ringers: 10 Five-Minute Warm-Ups to Start Any Class",
  "excerpt": "Bell ringers set the tone for the whole lesson. Here are ten quick geography warm-ups that get students thinking the moment they sit down — most with no prep at all.",
  "date": "2026-06-04",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1650526087824-163941841b52?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxnZW9ncmFwaHklMjBjbGFzc3Jvb20lMjBtYXB8ZW58MXwwfHx8MTc4MDU2MDQ3NXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "A bell ringer — sometimes called a 'do now' or starter — is the short task waiting for students the moment they walk in. Done well, it settles the room, builds a routine, and activates prior knowledge before the real lesson begins. Geography happens to be a perfect subject for it."
    },
    {
      "type": "heading",
      "text": "Why Geography Makes a Great Bell Ringer"
    },
    {
      "type": "paragraph",
      "text": "Geography is visual, low-stakes, and endlessly varied, so you rarely run out of fresh prompts. Better still, a daily five-minute dose works like spaced repetition: over a term, students quietly build a mental map of the world without ever sitting a formal test. Revisit earlier questions to check which ideas need another explanation."
    },
    {
      "type": "heading",
      "text": "10 Warm-Ups You Can Run Tomorrow"
    },
    {
      "type": "list",
      "items": [
        "Map of the Day — project a country outline and have students name it plus one fact.",
        "Where in the World — show a satellite or street-level image and ask students to guess the region, justifying it with visible clues.",
        "Flag of the Day — identify the flag, then name the capital.",
        "Capital Quick-Fire — five countries on the board, capitals written in 90 seconds.",
        "Odd One Out — four places; which does not belong, and why? This one forces reasoning, not just recall.",
        "Coordinate Hunt — give a latitude and longitude and have students find what is there.",
        "Headline Geography — put up a current-events headline and locate it on the wall map.",
        "Biggest or Smallest — rank four countries by area or population.",
        "Border Riddle — 'I border France and Spain and I am tiny' (Andorra).",
        "Mystery Region — describe a landscape such as 'fjords and reindeer' and have students name it."
      ]
    },
    {
      "type": "heading",
      "text": "Adjust for the Grade Level"
    },
    {
      "type": "paragraph",
      "text": "The same formats stretch across ages with small tweaks. For younger elementary students, lean on flags, continents, and 'find the country that is shaped like a boot' — recognition over recall. For middle school, layer in capitals, landforms, and simple 'why' questions. For high school, push toward reasoning: ask students to defend a guess with evidence, connect a headline to physical or human geography, or argue which of two countries has the bigger economy and why. The activity stays the same; the cognitive demand rises."
    },
    {
      "type": "heading",
      "text": "Tie It to What You're Teaching"
    },
    {
      "type": "paragraph",
      "text": "Bell ringers do double duty when they preview the day's lesson. Teaching rivers? Make the warm-up a 'name this river from its delta' image. Starting a unit on climate? Have students match four photos to their climate zones. A starter that quietly seeds the topic means the class arrives already thinking in the right direction, and you spend less time on cold introductions."
    },
    {
      "type": "heading",
      "text": "Make It a Routine"
    },
    {
      "type": "paragraph",
      "text": "Consistency is what makes bell ringers work. Use the same slot every day, have the prompt on screen as students enter, keep it to three to five minutes, and finish with a quick share-out. Rotate the types so nobody gets bored, and once students know the formats, let them submit prompts of their own — that ownership is half the battle. Pairing the starter with a one-line exit ticket at the end bookends the lesson without extra marking."
    },
    {
      "type": "heading",
      "text": "Build a Prompt Bank"
    },
    {
      "type": "paragraph",
      "text": "The only real failure mode is running out of ideas at eight in the morning. Beat it by keeping a running slide deck or folder of prompts you can pull from in seconds — one slide per starter, sorted by type. Add to it whenever you stumble on a striking image or a good 'odd one out'. Within a few weeks you will have a term's worth of warm-ups that need zero morning preparation. Swap prompts with a colleague who teaches the same subject and your bank doubles overnight, and a shared folder means a substitute teacher can run the starter without missing a beat."
    },
    {
      "type": "heading",
      "text": "Turn It Into a Game"
    },
    {
      "type": "paragraph",
      "text": "A single round of a satellite-guessing game makes a ready-made 'Where in the World' starter. Drop one location on the projector, let the class call out guesses, and ask a few students to defend their reasoning before the answer is revealed. It takes under five minutes and gets everyone arguing about clues — exactly the energy you want at the start of a lesson. EarthGuessr works well for this, and no two locations are ever the same, so the warm-up never goes stale."
    }
  ]
};
